import { Badge } from '@/shared/components/ui/badge';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import type { DesignationChangeSummary } from '../../types/admin-advisor.type';
import type { AdvisorEditorFormData } from './advisorEditor.types';
import { AdvisorEditorSection } from './AdvisorEditorSection';
import { formatDesignationChange } from './utils/advisorDesignationFormat';

type AdvisorDesignationsSectionProps = {
  formData: Pick<AdvisorEditorFormData, 'firmSizeSegment' | 'isFirmChampion'>;
  error?: string;
  mostRecentDesignationChange?: DesignationChangeSummary | null;
  onChange: <K extends keyof AdvisorEditorFormData>(
    field: K,
    value: AdvisorEditorFormData[K],
  ) => void;
};

const FIRM_SIZE_OPTIONS: ReadonlyArray<{ value: 'SMB' | 'ENT'; label: string }> = [
  { value: 'SMB', label: 'SMB — Small / mid-sized firms' },
  { value: 'ENT', label: 'ENT — Enterprise firms' },
];

/**
 * Internal admin designations (Feature 071): firm-size segment (required radio)
 * and the Firm Champion toggle. Admin-only; not shown to advisors.
 */
export const AdvisorDesignationsSection = ({
  formData,
  error,
  mostRecentDesignationChange,
  onChange,
}: AdvisorDesignationsSectionProps) => (
  <AdvisorEditorSection title="Designations (Internal)">
    <div className="mb-4 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600">Current:</span>
        {formData.firmSizeSegment ? (
          <Badge variant="secondary">{formData.firmSizeSegment}</Badge>
        ) : (
          <Badge variant="outline">Not set</Badge>
        )}
        {formData.isFirmChampion ? <Badge variant="secondary">Firm Champion</Badge> : null}
      </div>
      {mostRecentDesignationChange ? (
        <p className="text-xs text-gray-500">{formatDesignationChange(mostRecentDesignationChange)}</p>
      ) : null}
    </div>

    <fieldset>
      <legend className="text-sm font-medium text-gray-900">
        Firm Size Segment <span className="text-red-600">*</span>
      </legend>
      <p className="mb-2 text-sm text-gray-600">
        Required. Internal classification — not visible to the advisor.
      </p>
      <div className="flex flex-col gap-2">
        {FIRM_SIZE_OPTIONS.map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="radio"
              name="firmSizeSegment"
              value={option.value}
              checked={formData.firmSizeSegment === option.value}
              onChange={() => onChange('firmSizeSegment', option.value)}
              aria-invalid={!!error}
              aria-describedby={error ? 'firmSizeSegment-error' : undefined}
            />
            {option.label}
          </label>
        ))}
      </div>
      {error ? (
        <p id="firmSizeSegment-error" role="alert" className="mt-1 text-sm text-red-600">
          {error}
        </p>
      ) : null}
    </fieldset>

    <div className="mt-4 flex items-center justify-between rounded-md border border-gray-200 p-4">
      <div>
        <Label htmlFor="isFirmChampion">Is Firm Champion?</Label>
        <p className="text-sm text-gray-600">
          Flags this advisor as their firm&apos;s champion. Admin-only; the advisor is not notified.
        </p>
      </div>
      <Switch
        id="isFirmChampion"
        checked={formData.isFirmChampion}
        onCheckedChange={(value) => onChange('isFirmChampion', value)}
      />
    </div>
  </AdvisorEditorSection>
);
